import { apiHiringPlan } from "@/api/recruit/hiring-plan.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { employeesKeys } from "@/queries/employees/use-employees.query.ts";
import { ApiErrorException, ApiPassException } from "@/types/api.types.ts";
import {
  ApiHiringPlanApproveRequest,
  ApiHiringPlanConfirmationCommentUpdate,
  ApiHiringPlanFilter,
  ApiHiringPlanPayload,
} from "@/types/recruit/hiring-plan.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const hiringPlanKeys = createQueryKeys("hiringPlan", {
  search: (filter: ApiHiringPlanFilter) => ({
    queryKey: [filter],
  }),
  myPositionRequests: () => ({
    queryKey: ["my_requests"],
  }),
  requestDetails: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (id === undefined) throw new ApiPassException("Request id is required");
      const result = await apiHiringPlan.getRequestById(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export interface SaveParams {
  id?: number;
  payload: ApiHiringPlanPayload;
}

export const useRequestPositionSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({ id, payload }: SaveParams) {
      const result = !id
        ? await apiHiringPlan.createRequest(payload)
        : await apiHiringPlan.updateRequest(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: hiringPlanKeys.search._def });
      void queryClient.invalidateQueries({ queryKey: employeesKeys.hiringPlanSearch._def });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useHiringPlanSearch = (filter: ApiHiringPlanFilter, enabled: boolean = true) => {
  const queryClient = useQueryClient();

  return useQuery({
    ...hiringPlanKeys.search(filter), // Явно указываем queryKey
    queryFn: async () => {
      const result = await apiHiringPlan.search(filter);
      if (result.error) throw new ApiErrorException(result.error);

      result.data?.items.forEach((item) => {
        if (item.approval_request_details) {
          queryClient.setQueryData(
            hiringPlanKeys.requestDetails(item.approval_request_details.id).queryKey,
            item
          );
        }
      });

      return result.data;
    },
    staleTime: 60 * 1000,
    enabled,
  });
};

export const usePositionRequestDetails = (id: number | undefined, enabled: boolean) => {
  return useQuery({
    ...hiringPlanKeys.requestDetails(id),
    enabled,
    staleTime: 60 * 1000,
  });
};

export const usePositionRequestAnswer = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      id,
      approve,
      payload,
    }: {
      id: number;
      approve: boolean;
      payload: ApiHiringPlanApproveRequest;
    }) => {
      const result = approve
        ? await apiHiringPlan.approveRequest(id, payload)
        : await apiHiringPlan.rejectRequest(id, payload);
      if (result.error) throw new ApiErrorException(result.error);
      return id;
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({
        queryKey: hiringPlanKeys.requestDetails(id).queryKey,
      });
      void queryClient.invalidateQueries({ queryKey: hiringPlanKeys.search._def });
      void queryClient.invalidateQueries({ queryKey: employeesKeys.hiringPlanSearch._def });
      void queryClient.invalidateQueries({
        queryKey: hiringPlanKeys.myPositionRequests().queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useApprovalRequestCancel = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiHiringPlan.cancelRequest(id);
      if (result.error) throw new ApiErrorException(result.error);
      return id;
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({
        queryKey: hiringPlanKeys.requestDetails(id).queryKey,
      });
      void queryClient.invalidateQueries({ queryKey: hiringPlanKeys.search._def });
      void queryClient.invalidateQueries({ queryKey: employeesKeys.hiringPlanSearch._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useHiringPlanConfirmationCommentUpdate = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: ApiHiringPlanConfirmationCommentUpdate;
    }) => {
      const result = await apiHiringPlan.updateConfirmationComment(id, payload);
      if (result.error) throw new ApiErrorException(result.error);
      return id;
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({ queryKey: hiringPlanKeys.requestDetails(id).queryKey });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useConnectPositionRequestToJob = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ requestId, jobId }: { requestId: number; jobId: number | null }) => {
      const result = await apiHiringPlan.connectRequestToJob(requestId, jobId);
      if (result.error) throw new ApiErrorException(result.error);
      return { requestId, jobId };
    },
    onSuccess: ({ requestId }) => {
      void queryClient.invalidateQueries({
        queryKey: hiringPlanKeys.requestDetails(requestId).queryKey,
      });
      void queryClient.invalidateQueries({ queryKey: hiringPlanKeys.search._def });
      void queryClient.invalidateQueries({ queryKey: employeesKeys.hiringPlanSearch._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useHiringPlanDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiHiringPlan.deleteRequest(id);
      if (result.error) throw new ApiErrorException(result.error);
      return id;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: hiringPlanKeys.search._def });
      void queryClient.invalidateQueries({ queryKey: employeesKeys.hiringPlanSearch._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
